import React, { useMemo, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import {
  AlarmGraphAppNameOptions,
  alarmGraphSimcardOptions,
  MainGraphTypes,
  SimcardsOnlineGraphTypes,
  TransactionsPayGraphTypes,
} from '@utils/alarmsConstants';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';

interface UseAlarmMenuFilterOptions {
  simcardsPermission?: RolesTypes;
  transactionsPermission?: RolesTypes;
  mode?: 'list' | 'create'; // 'list' para as páginas de lista, 'create' para CreateAlarmStepOne
}

export const useAlarmMenuFilter = (options?: UseAlarmMenuFilterOptions) => {
  const { t } = useTranslation('create-alarm-step');
  const { effectiveRoles } = useContext(SessionContext);
  const mode = options?.mode || 'list';

  const defaultSimcardsPermission =
    mode === 'create'
      ? rolesConstants.INSIGHTS_ALARM_MANAGE_CONFIGURATION
      : rolesConstants.INSIGHTS_ALARM_CONFIGURATION;

  const defaultTransactionsPermission =
    mode === 'create'
      ? rolesConstants.INSIGHTS_TRANSACTIONS_ALARM_MANAGE_CONFIGURATION
      : rolesConstants.INSIGHTS_TRANSACTIONS_VIEW;

  const hasSimcardsPermission = effectiveRoles?.portal?.includes(
    options?.simcardsPermission || defaultSimcardsPermission,
  );

  const hasTransactionsPermission = effectiveRoles?.portal?.includes(
    options?.transactionsPermission || defaultTransactionsPermission,
  );

  const allMenuFilter = useMemo(() => {
    const menuOptions = [];

    if (hasSimcardsPermission) {
      menuOptions.push({
        label: (
          <span style={{ fontSize: '1rem' }}>
            {t('stepOne.createAlarmSimcardsOnlineTitle')}
          </span>
        ),
        title: MainGraphTypes.SIMCARDS_ONLINE,
        options:
          mode === 'create'
            ? [
                {
                  label: <span>{t('stepOne.createAlarmTechnologyField')}</span>,
                  value: SimcardsOnlineGraphTypes.TECHNOLOGY_GRAPH,
                },
                {
                  label: <span>{t('stepOne.createAlarmOperatorField')}</span>,
                  value: SimcardsOnlineGraphTypes.OPERATOR_GRAPH,
                },
                {
                  label: (
                    <span>{t('stepOne.createAlarmMacroregionField')}</span>
                  ),
                  value: SimcardsOnlineGraphTypes.MACROREGIAO_GRAPH,
                },
                {
                  label: <span>{t('stepOne.createAlarmStateField')}</span>,
                  value: SimcardsOnlineGraphTypes.STATE_GRAPH,
                },
              ]
            : [
                {
                  label: <span>{t('stepOne.createAlarmTechnologyField')}</span>,
                  value: alarmGraphSimcardOptions.SIMCARD_TECHNOLOGY,
                },
                {
                  label: <span>{t('stepOne.createAlarmOperatorField')}</span>,
                  value: alarmGraphSimcardOptions.SIMCARD_OPERATOR,
                },
                {
                  label: (
                    <span>{t('stepOne.createAlarmMacroregionField')}</span>
                  ),
                  value: alarmGraphSimcardOptions.SIMCARD_MACROREGIAO,
                },
                {
                  label: <span>{t('stepOne.createAlarmStateField')}</span>,
                  value: alarmGraphSimcardOptions.SIMCARD_STATE,
                },
              ],
      });
    }

    if (hasTransactionsPermission) {
      menuOptions.push({
        label: (
          <span style={{ fontSize: '1rem' }}>
            {t('stepOne.createAlarmTransactionsTitle')}
          </span>
        ),
        title: MainGraphTypes.TRANSACTIONS_PAY,
        options:
          mode === 'create'
            ? [
                {
                  label: (
                    <span>{t('stepOne.createAlarmAverageTimeField')}</span>
                  ),
                  value: TransactionsPayGraphTypes.AVERAGE_TIME,
                },
                {
                  label: (
                    <span>
                      {t('stepOne.createAlarmTransactionsByStatusField')}
                    </span>
                  ),
                  value: TransactionsPayGraphTypes.TRANSACTIONS_BY_STATUS,
                },
                {
                  label: (
                    <span>
                      {t('stepOne.createAlarmTransactionsHistoryField')}
                    </span>
                  ),
                  value: TransactionsPayGraphTypes.TRANSACTION_HISTORY,
                },
                {
                  label: (
                    <span>
                      {t('stepOne.createAlarmTransactionsStatusField')}
                    </span>
                  ),
                  value: TransactionsPayGraphTypes.TRANSACTION_STATUS,
                },
                {
                  label: (
                    <span>
                      {t('stepOne.createAlarmTransactionsByTypeField')}
                    </span>
                  ),
                  value: TransactionsPayGraphTypes.TRANSACTIONS_BY_TYPE,
                },
              ]
            : [
                {
                  label: (
                    <span>{t('stepOne.createAlarmAverageTimeField')}</span>
                  ),
                  value: AlarmGraphAppNameOptions.APPNAME_AVERAGE_TIME,
                },
                {
                  label: (
                    <span>
                      {t('stepOne.createAlarmTransactionsByStatusField')}
                    </span>
                  ),
                  value:
                    AlarmGraphAppNameOptions.APPNAME_TRANSACTIONS_BY_STATUS,
                },
                {
                  label: (
                    <span>
                      {t('stepOne.createAlarmTransactionsHistoryField')}
                    </span>
                  ),
                  value: AlarmGraphAppNameOptions.APPNAME_TRANSACTION_HISTORY,
                },
                {
                  label: (
                    <span>
                      {t('stepOne.createAlarmTransactionsStatusField')}
                    </span>
                  ),
                  value: AlarmGraphAppNameOptions.APPNAME_TRANSACTION_STATUS,
                },
                {
                  label: (
                    <span>
                      {t('stepOne.createAlarmTransactionsByTypeField')}
                    </span>
                  ),
                  value: AlarmGraphAppNameOptions.APPNAME_TRANSACTIONS_BY_TYPE,
                },
              ],
      });
    }

    return menuOptions;
  }, [hasSimcardsPermission, hasTransactionsPermission, t, mode]);

  const defaultFilterValue = useMemo(() => {
    if (hasSimcardsPermission) {
      return mode === 'create'
        ? SimcardsOnlineGraphTypes.PRODUCT_GRAPH
        : alarmGraphSimcardOptions.SIMCARD_PRODUCT;
    }
    if (hasTransactionsPermission) {
      return mode === 'create'
        ? TransactionsPayGraphTypes.AVERAGE_TIME
        : AlarmGraphAppNameOptions.APPNAME_AVERAGE_TIME;
    }
    return mode === 'create'
      ? SimcardsOnlineGraphTypes.PRODUCT_GRAPH
      : alarmGraphSimcardOptions.SIMCARD_PRODUCT;
  }, [hasSimcardsPermission, hasTransactionsPermission, mode]);

  return {
    allMenuFilter,
    defaultFilterValue,
    hasSimcardsPermission,
    hasTransactionsPermission,
  };
};
